import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Collapse,
  Backdrop,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionIcon from "@mui/icons-material/Description";
import SyncIcon from "@mui/icons-material/Sync";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UpdateIcon from "@mui/icons-material/Update";
import SpeedIcon from "@mui/icons-material/Speed";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../../api/client";
import useDebounce from "../../hooks/useDebounce";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { OverAllLoader } from "../../components/OverAllLoader";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import RegenerateAnswerDialog from "../../components/RegenerateAnswerDialog";
import pdfIcon from "../../assets/images/pdf.png";
import docIcon from "../../assets/images/doc.png";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

// Themeable markdown renderer (bold, code, lists, headings) used for questions & answers
const MarkdownContent = ({
  text,
  color = "#1b5e20",
  strongColor = "#14532d",
  headingColor = "#14532d",
  codeBg = "#c8e6c9",
  codeColor = "#14532d",
  fontSize = "14px",
  fontWeight = 400,
  pMb = 1,
}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => (
        <Typography sx={{ fontSize, fontWeight, color, lineHeight: 1.7, mb: pMb }}>
          {children}
        </Typography>
      ),
      strong: ({ children }) => (
        <Box component="strong" sx={{ fontWeight: 700, color: strongColor }}>
          {children}
        </Box>
      ),
      em: ({ children }) => (
        <Box component="em" sx={{ fontStyle: "italic" }}>
          {children}
        </Box>
      ),
      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 3, mb: 1, mt: 0.5 }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 3, mb: 1, mt: 0.5 }}>
          {children}
        </Box>
      ),
      li: ({ children }) => (
        <Typography component="li" sx={{ fontSize, color, lineHeight: 1.7, mb: 0.5 }}>
          {children}
        </Typography>
      ),
      h1: ({ children }) => (
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: headingColor, mt: 1.5, mb: 0.5 }}>
          {children}
        </Typography>
      ),
      h2: ({ children }) => (
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: headingColor, mt: 1.5, mb: 0.5 }}>
          {children}
        </Typography>
      ),
      h3: ({ children }) => (
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: headingColor, mt: 1.5, mb: 0.5 }}>
          {children}
        </Typography>
      ),
      code({ inline, children }) {
        const value = String(children);
        const isBlock = !inline && value.includes("\n");
        if (!isBlock) {
          return (
            <Box
              component="span"
              sx={{
                backgroundColor: codeBg,
                color: codeColor,
                borderRadius: "6px",
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: "13px",
                px: 0.8,
                py: 0.2,
                mx: 0.3,
              }}
            >
              {value}
            </Box>
          );
        }
        return (
          <Box
            sx={{
              backgroundColor: "#1e293b",
              color: "#f1f5f9",
              p: 1.5,
              my: 1.5,
              borderRadius: "8px",
              overflowX: "auto",
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              fontSize: "12px",
              border: "1px solid #334155",
            }}
          >
            <pre style={{ margin: 0 }}>
              <code>{value}</code>
            </pre>
          </Box>
        );
      },
    }}
  >
    {text}
  </ReactMarkdown>
);

// Green-styled markdown for the answer card
const MarkdownAnswer = ({ text }) => <MarkdownContent text={text} />;

// Dark, bold markdown for the question text
const MarkdownQuestion = ({ text }) => (
  <MarkdownContent
    text={text}
    color="#333"
    strongColor="#111"
    headingColor="#03438A"
    codeBg="#eef2f7"
    codeColor="#03438A"
    fontSize="16px"
    fontWeight={700}
    pMb={0}
  />
);

// Typed text for a configuration field. It stays in the field while the user types and is handed
// to the page (onCommit) after a short pause or when the field loses focus, so a keystroke
// re-renders only the field instead of the whole page (holding a key used to end in
// "Maximum update depth exceeded").
const useCommittedText = (value, onCommit) => {
  const [text, setText] = useState(value ?? "");
  const debouncedText = useDebounce(text, 300);
  const committedRef = useRef(value ?? "");

  // Follow changes made outside the field (form reset, edit prefill)
  useEffect(() => {
    if ((value ?? "") !== committedRef.current) {
      committedRef.current = value ?? "";
      setText(value ?? "");
    }
  }, [value]);

  const commit = (next) => {
    if (next === committedRef.current) return;
    committedRef.current = next;
    onCommit(next);
  };

  // Hand the text over once the user pauses typing
  useEffect(() => {
    commit(debouncedText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);

  return { text, setText, commit };
};

// TextField for the typed configuration fields (Description, No. of Questions, Marks, Test Case Count)
const CommitTextField = ({ value, onCommit, ...props }) => {
  const { text, setText, commit } = useCommittedText(value, onCommit);
  return (
    <TextField
      {...props}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => commit(text)}
    />
  );
};

// Subject topic field: type freely or pick a suggested topic
const TopicAutocomplete = ({ value, options, disabled, placeholder, error, helperText, sx, onCommit }) => {
  const { text, setText, commit } = useCommittedText(value, onCommit);
  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      value={value || ""}
      inputValue={text}
      onChange={(_, newValue) => {
        setText(newValue || "");
        commit(newValue || "");
      }}
      onInputChange={(_, newInputValue) => setText(newInputValue)}
      onBlur={() => commit(text)}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} error={error} helperText={helperText} sx={sx} />
      )}
    />
  );
};

// Premium full-screen overlay shown while questions are being generated / retried
const AddCategory = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { edit, newCategory } = location.state || "";

  const [categories, setCategories] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [bloomsOptions, setBloomsOptions] = useState([]);
  const [labTools, setLabTools] = useState([]);
  const [topicChips, setTopicChips] = useState([]);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [sessionKeys, setSessionKeys] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [feedbackDescription, setFeedbackDescription] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionFeedback, setQuestionFeedback] = useState({});
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const [regeneratingAnswers, setRegeneratingAnswers] = useState({}); // { [quiz_id]: true }
  // Regenerate-answer prompt dialog — opens from the per-question regenerate
  // IconButton; the shared RegenerateAnswerDialog owns the preset/custom state.
  const [regenDialog, setRegenDialog] = useState({ open: false, quizId: null, index: null, question: "" });
  const [editingQuestions, setEditingQuestions] = useState({});
  const [editSnapshots, setEditSnapshots] = useState({});
  // Premium generation overlay state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState("");
  const [generationTitle, setGenerationTitle] = useState("");
  // Streaming (SSE) generation state — one entry per subcategory card
  const [isStreaming, setIsStreaming] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [streamCards, setStreamCards] = useState([]); // [{ key, subcategory, status, message, retry_after }]
  // Refs avoid stale closures while consuming the stream
  const streamQuestionsRef = useRef([]);
  const streamKeysRef = useRef([]);
  const streamDoneRef = useRef(false);
  const streamAbortRef = useRef(null);
  const lastPayloadRef = useRef(null);

  const toggleAnswer = (key) =>
    setExpandedAnswers((prev) => ({ ...prev, [key]: !prev[key] }));

  // Ask the backend to regenerate the answer for a single question. The endpoint
  // accepts a list of quiz_ids; we use one at a time to keep the per-question
  // spinner accurate. Optional `prompt` steers the regeneration (preset string
  // or custom trainer-written instructions); when null/empty the backend uses
  // its default prompt. On success the answer is replaced inline and persisted.
  const regenerateAnswer = async (quizId, index, prompt = null) => {
    if (!quizId) {
      showSnackbar("error", "This question has no quiz_id yet — try saving first.");
      return;
    }
    setRegeneratingAnswers((prev) => ({ ...prev, [quizId]: true }));
    try {
      const payload = { quiz_ids: [quizId] };
      const trimmed = typeof prompt === "string" ? prompt.trim() : null;
      if (trimmed) payload.prompt = trimmed;
      const res = await api.post("RegenerateAnswers", payload);
      const data = res?.data;
      const updated = Array.isArray(data?.updated) ? data.updated : [];
      const match = updated.find((u) => u.quiz_id === quizId);
      if (data?.status === "success" && match) {
        setGeneratedQuestions((prev) => {
          const next = prev.map((qq, i) =>
            i === index ? { ...qq, answer: match.answer } : qq
          );
          try {
            localStorage.setItem("generatedQuestions", JSON.stringify(next));
          } catch (storageError) {
            console.warn("Failed to persist regenerated answer:", storageError);
          }
          return next;
        });
        // Auto-expand so the trainer sees the new answer immediately
        setExpandedAnswers((prev) => ({ ...prev, [quizId]: true }));
        showSnackbar("success", data.message || "Answer regenerated.");
      } else {
        showSnackbar(
          "error",
          data?.message || "Could not regenerate the answer. Please try again."
        );
      }
    } catch (error) {
      showSnackbar(
        "error",
        error?.response?.data?.message ||
        error?.message ||
        "Failed to regenerate the answer."
      );
    } finally {
      setRegeneratingAnswers((prev) => {
        const next = { ...prev };
        delete next[quizId];
        return next;
      });
    }
  };

  // Open the shared prompt-picker dialog for a specific question.
  const openRegenDialog = (quizId, index, question) => {
    if (!quizId) {
      showSnackbar("error", "This question has no quiz_id yet — try saving first.");
      return;
    }
    setRegenDialog({ open: true, quizId, index, question: question || "" });
  };

  const closeRegenDialog = () =>
    setRegenDialog({ open: false, quizId: null, index: null, question: "" });

  // Edit a generated question's field in place and persist to localStorage
  const updateQuestionField = (index, field, value) => {
    setGeneratedQuestions((prev) => {
      const next = prev.map((q, i) => (i === index ? { ...q, [field]: value } : q));
      try {
        localStorage.setItem("generatedQuestions", JSON.stringify(next));
      } catch (storageError) {
        console.warn("Failed to persist edited question:", storageError);
      }
      return next;
    });
  };

  // Edit a single MCQ option's text and persist to localStorage
  const updateOptionText = (qIndex, optIndex, value) => {
    setGeneratedQuestions((prev) => {
      const next = prev.map((q, i) =>
        i === qIndex
          ? {
            ...q,
            options: q.options.map((o, oi) =>
              oi === optIndex ? { ...o, text: value } : o
            ),
          }
          : q
      );
      try {
        localStorage.setItem("generatedQuestions", JSON.stringify(next));
      } catch (storageError) {
        console.warn("Failed to persist edited option:", storageError);
      }
      return next;
    });
  };

  // MCQ options array -> raw "\n"-delimited string e.g. "(a) O(1)\n(b) O(log n)"
  const optionsToString = (options) =>
    Array.isArray(options) && options.length
      ? options
        .map((o) => `(${(o.label || "").toLowerCase()}) ${o.text || ""}`)
        .join("\n")
      : null;

  // Enter edit mode; snapshot the original so we can send only what changed
  const startEditQuestion = (q, key) => {
    setEditSnapshots((prev) => ({ ...prev, [key]: q }));
    setEditingQuestions((prev) => ({ ...prev, [key]: true }));
  };

  // On "Done": diff against the snapshot and persist changes via EditQuizQuestion
  const saveEditQuestion = async (index, key) => {
    const q = generatedQuestions[index];
    if (!q) {
      setEditingQuestions((prev) => ({ ...prev, [key]: false }));
      return;
    }
    const snap = editSnapshots[key] || q;

    if (!q.quiz_id) {
      showSnackbar("error", "Cannot update: missing quiz_id");
      setEditingQuestions((prev) => ({ ...prev, [key]: false }));
      return;
    }

    const payload = { quiz_id: q.quiz_id };
    let changed = false;

    if ((q.question ?? "") !== (snap.question ?? "")) {
      payload.question = q.question ?? "";
      changed = true;
    }

    // options: only meaningful for MCQ; send as raw \n-delimited string
    const isMCQ = (q.question_type || "").toLowerCase() === "mcq";
    if (isMCQ) {
      const curOpts = optionsToString(q.options);
      if (curOpts !== optionsToString(snap.options)) {
        payload.options = curOpts;
        changed = true;
      }
    }

    if ((q.answer ?? "") !== (snap.answer ?? "")) {
      payload.answer = q.answer ?? "";
      changed = true;
    }

    if (!changed) {
      // Nothing edited — just leave edit mode without hitting the API
      setEditingQuestions((prev) => ({ ...prev, [key]: false }));
      return;
    }

    setLoading(true);
    try {
      console.log("Calling EditQuizQuestion with payload:", payload);
      const res = await api.post("EditQuizQuestion", payload);
      console.log("EditQuizQuestion Response:", res?.data);

      if (res?.data?.status === "success") {
        const u = res.data;
        setGeneratedQuestions((prev) => {
          const next = prev.map((qq, i) =>
            i === index
              ? {
                ...qq,
                ...(u.question !== undefined ? { question: u.question } : {}),
                ...(u.options !== undefined ? { options: u.options } : {}),
                ...(u.answer !== undefined ? { answer: u.answer } : {}),
                ...(u.marks !== undefined ? { marks: u.marks } : {}),
                ...(u.blooms_level !== undefined ? { blooms_level: u.blooms_level } : {}),
                ...(u.level !== undefined ? { level: u.level } : {}),
                ...(u.question_type !== undefined ? { question_type: u.question_type } : {}),
              }
              : qq
          );
          try {
            localStorage.setItem("generatedQuestions", JSON.stringify(next));
          } catch (storageError) {
            console.warn("Failed to persist updated question:", storageError);
          }
          return next;
        });
        showSnackbar("success", u.message || "Question updated successfully");
        setEditingQuestions((prev) => ({ ...prev, [key]: false }));
      } else {
        // Keep edit mode open so the user can retry
        showSnackbar("error", res?.data?.message || "Failed to update question");
      }
    } catch (error) {
      showSnackbar(
        "error",
        error?.response?.data?.message || error?.message || "Failed to update question"
      );
    } finally {
      setLoading(false);
    }
  };
  const [imagePreview, setImagePreview] = useState(null);
  const [interpretedImagePreview, setInterpretedImagePreview] = useState(null);
  const [createThroughReferenceImage, setCreateThroughReferenceImage] = useState(false);
  const [showManualCreateCard, setShowManualCreateCard] = useState(false);
  const [manualDescription, setManualDescription] = useState(
    "This is a pre-bound sample description for the uploaded reference diagram."
  );
  const [interpretationData, setInterpretationData] = useState(null);

  // Unique id generator for cards / pairs
  const uidRef = useRef(1);
  const nextId = () => (uidRef.current += 1);

  const createEmptyCard = () => ({
    id: nextId(),
    subCategoryDescriptionPairs: [
      { id: nextId(), subCategory: "", description: "" },
    ],
    level: null,
    questionType: "",
    questionsCount: "",
    giveMarks: "",
    bloomsLevels: [],
    toolRequirement: null, // selected lab tool object { id, tool_name } | null
  });

  // Each card is one question-configuration sharing the common Program/Subject/Reference
  const [cards, setCards] = useState(() => [createEmptyCard()]);
  const [cardErrors, setCardErrors] = useState({});

  const issues = [
    { label: "Question is unclear", value: "Question is unclear", icon: <HelpOutlineIcon fontSize="small" /> },
    { label: "Content is outdated", value: "Content is outdated", icon: <UpdateIcon fontSize="small" /> },
    { label: "Wrong difficulty level", value: "Wrong difficulty level", icon: <SpeedIcon fontSize="small" /> },
    { label: "Other", value: "Other", icon: <MoreHorizIcon fontSize="small" /> },
  ];

  const levels = [
    { id: 1, name: "Beginner" },
    { id: 2, name: "Intermediate" },
    { id: 3, name: "Advanced" },
  ];

  const questionTypes = [
    "MCQ",
    "Descriptive",
    "Fill in the blanks",
    "Programming-based",
  ];

  // Total questions across ALL configuration cards must not exceed this
  const MAX_TOTAL_QUESTIONS = 25;
  const MAX_CONFIGURATIONS = 10;


  const schema = Yup.object().shape({
    program: Yup.object()
      .nullable()
      .required(`Please select a ${getLabel("PROGRAMS")}`)
      .typeError(`Please select a ${getLabel("PROGRAMS")}`),
    category: Yup.object().required(`Please select a ${getLabel("SUBJECT")}`),
    referenceImage: Yup.mixed()
      .nullable()
      .test("fileSize", "File size must be less than 5 MB", (value) => {
        if (!value) return true; // attachment is optional
        return value.size <= 5 * 1024 * 1024;
      }),
  });



  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      program: null,
      category: null,
      referenceImage: null,
    },
  });

  const fetchCategoriesByProgram = async (programId) => {
    if (!programId) {
      setCategories([]);
      return;
    }
    setLoading(true);
    try {
      // Role-aware (INSTITUTE_ADMIN sees institute categories; TRAINER sees mapped
      // categories, falling back to all under the program). Backend filters by JWT.
      const res = await api.post("GetAssignedCategoriesDropdown", {
        program_id: programId,
      });
      if (res?.data?.status === "success") {
        setCategories(res?.data?.data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.log("Error fetching categories by program:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const getSubcategories = async (categoryId) => {
    setLoading(true);
    try {
      const payload = { category_id: categoryId };
      const res = await api.post("ListSubCategoriesByCategory", payload);
      if (res?.data?.status === "success") {
        setTopicChips(res?.data?.data || []);
      }
    } catch (error) {
      console.log("Error :", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      // Role-aware programs for the logged-in user (no body; identified via JWT).
      const res = await api.post("GetAssignedProgramsDropdown", {});
      if (res?.data?.status === "success") {
        setPrograms(res?.data?.data || []);
      }
    } catch (error) {
      console.log("Error fetching programs:", error);
    }
  };

  const fetchBloomsLevels = async () => {
    try {
      const res = await api.post("GetBloomsLevels", {});
      if (res?.data?.status === "success") {
        setBloomsOptions(res?.data?.data || []);
      }
    } catch (error) {
      console.log("Error fetching Bloom's levels:", error);
    }
  };

  // LabToolDropdown returns a bare array: [{ id, tool_name }, ...] (ordered by name).
  // The selected id is sent as `lab_tool_id` on each subcategory card.
  const fetchLabTools = async () => {
    try {
      const res = await api.post("LabToolDropdown", {});
      const data = res?.data;
      if (Array.isArray(data)) {
        setLabTools(data);
      } else if (Array.isArray(data?.data)) {
        setLabTools(data.data);
      }
    } catch (error) {
      console.log("Error fetching lab tools:", error);
    }
  };



  useEffect(() => {
    // Reset form and localStorage if coming from Settings with newCategory
    if (newCategory) {
      localStorage.removeItem("generatedQuestions");
      localStorage.removeItem("sessionKeys");
      localStorage.removeItem("formData");
      localStorage.removeItem("questionFeedback");
      setGeneratedQuestions([]);
      setSessionKeys([]);
      setQuestionFeedback({});
      setCards([createEmptyCard()]);
      setCardErrors({});
      setShowManualCreateCard(false);
      setInterpretationData(null);
      setInterpretedImagePreview(null);
      setStreamCards([]);
      setGenerationComplete(false);
      streamQuestionsRef.current = [];
      streamKeysRef.current = [];
      reset();
    }

    fetchPrograms();
    fetchBloomsLevels();
    fetchLabTools();
    // Load cached data from localStorage
    try {
      const cachedQuestions = localStorage.getItem("generatedQuestions");
      const cachedSessionKeys = localStorage.getItem("sessionKeys");
      const cachedFormData = localStorage.getItem("formData");
      const cachedFeedback = localStorage.getItem("questionFeedback");

      if (cachedQuestions) {
        setGeneratedQuestions(JSON.parse(cachedQuestions));
      }
      if (cachedSessionKeys) {
        setSessionKeys(JSON.parse(cachedSessionKeys));
      }
      if (cachedFormData) {
        const formData = JSON.parse(cachedFormData);
        if (formData.program !== undefined) setValue("program", formData.program);
        setValue("category", formData.category);
        // Restore configuration cards
        if (Array.isArray(formData.cards) && formData.cards.length > 0) {
          setCards(formData.cards);
        }
        // Re-populate categories dropdown for the cached program
        if (formData.program?.program_id) {
          fetchCategoriesByProgram(formData.program.program_id);
        }
        // Load subcategories if category exists (support old "id" + new "category_id")
        const cachedCategoryId =
          formData.category?.category_id ?? formData.category?.id;
        if (cachedCategoryId) {
          getSubcategories(cachedCategoryId);
        }
      }
      if (cachedFeedback) {
        setQuestionFeedback(JSON.parse(cachedFeedback));
      }
    } catch (parseError) {
      console.error("Failed to parse cached form or question data:", parseError);
    }
  }, [newCategory]);

  // Abort any in-flight question stream if the user leaves the screen.
  useEffect(() => {
    return () => {
      if (streamAbortRef.current) streamAbortRef.current.abort();
    };
  }, []);



  // ---- Card helpers ----
  const getCardErr = (cardId) => cardErrors[cardId] || {};

  const clearCardFieldError = (cardId, field) => {
    setCardErrors((prev) => {
      if (!prev[cardId]) return prev;
      return { ...prev, [cardId]: { ...prev[cardId], [field]: "" } };
    });
  };

  const updateCardField = (cardId, field, value) => {
    setCards((prev) =>
      prev.map((c) => (c.id === cardId ? { ...c, [field]: value } : c))
    );
    clearCardFieldError(cardId, field);
  };

  const updatePair = (cardId, pairId, field, value) => {
    setCards((prev) =>
      prev.map((c) =>
        c.id === cardId
          ? {
            ...c,
            subCategoryDescriptionPairs: c.subCategoryDescriptionPairs.map(
              (p) => (p.id === pairId ? { ...p, [field]: value } : p)
            ),
          }
          : c
      )
    );
    // Clear validation error for this pair field
    setCardErrors((prev) => {
      // No error to clear: keep the same state so typing doesn't trigger an extra re-render
      if (!prev[cardId]?.pairs?.[pairId]?.[field]) return prev;
      const ce = { ...(prev[cardId] || {}) };
      const pairs = { ...(ce.pairs || {}) };
      pairs[pairId] = { ...(pairs[pairId] || {}), [field]: "" };
      ce.pairs = pairs;
      return { ...prev, [cardId]: ce };
    });
  };

  const duplicateCard = (cardId) => {
    setCards((prev) => {
      const idx = prev.findIndex((c) => c.id === cardId);
      if (idx === -1 || prev.length >= MAX_CONFIGURATIONS) return prev;
      // Add a fresh empty configuration right after the clicked card
      const next = [...prev];
      next.splice(idx + 1, 0, createEmptyCard());
      return next;
    });
  };

  const removeCard = (cardId) => {
    setCards((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== cardId) : prev));
    setCardErrors((prev) => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
  };

  const validateCards = () => {
    const errs = {};
    let ok = true;
    cards.forEach((card) => {
      const ce = { pairs: {} };
      let hasValidPair = false;

      card.subCategoryDescriptionPairs.forEach((pair) => {
        if (pair.subCategory) hasValidPair = true;
        const pe = {};
        const topic = (pair.subCategory || "").trim();
        if (!topic) {
          pe.subCategory = `${getLabel("SUBJECT")} topic is required`;
        } else if (/^\d+$/.test(topic)) {
          pe.subCategory = `${getLabel("SUBJECT")} topic must contain letters and cannot contain only numbers.`;
        } else if (!/[A-Za-z0-9]/.test(topic)) {
          pe.subCategory = `${getLabel("SUBJECT")} topic must contain letters and cannot contain only special characters.`;
        } else if (topic.length < 3) {
          pe.subCategory = `${getLabel("SUBJECT")} topic must be at least 3 characters long.`;
        } else if (topic.length > 50) {
          pe.subCategory = `${getLabel("SUBJECT")} topic cannot exceed 50 characters.`;
        }

        const description = (pair.description || "").trim();
        if (description) {
          if (/^\d+$/.test(description)) {
            pe.description = "Description must contain letters and cannot contain only numbers.";
          } else if (!/[A-Za-z0-9]/.test(description)) {
            pe.description = "Description must contain letters and cannot contain only special characters.";
          } else if (description.length < 3) {
            pe.description = "Description must be at least 3 characters long.";
          } else if (description.length > 100) {
            pe.description = "Description cannot exceed 100 characters.";
          }
        }
        if (Object.keys(pe).length) ce.pairs[pair.id] = pe;
      });

      if (!card.level) ce.level = "Please select a level";
      if (!card.questionType) ce.questionType = "Please select a question type";

      if (card.questionsCount === "" || card.questionsCount === null || card.questionsCount === undefined) {
        ce.questionsCount = "Please enter number of questions";
      } else if (Number(card.questionsCount) < 1) {
        ce.questionsCount = "Please enter at least 1 question";
      } else if (Number(card.questionsCount) > 25) {
        ce.questionsCount = "Maximum 25 questions allowed";
      }

      if (card.giveMarks === "" || card.giveMarks === null || card.giveMarks === undefined) {
        ce.giveMarks = "Please enter marks";
      } else if (Number(card.giveMarks) < 1) {
        ce.giveMarks = "Marks must be at least 1";
      } else if (Number(card.giveMarks) > 20) {
        ce.giveMarks = "Maximum 20 marks allowed";
      }

      if (!card.bloomsLevels || card.bloomsLevels.length === 0) {
        ce.bloomsLevels = "Please select at least one Bloom's Taxonomy Level";
      }

      if (!hasValidPair && Object.keys(ce.pairs).length === 0) {
        ce.general = "Please fill at least one subject topic";
      }

      const hasError =
        Object.keys(ce.pairs).length ||
        ce.level ||
        ce.questionType ||
        ce.questionsCount ||
        ce.giveMarks ||
        ce.bloomsLevels ||
        ce.general;

      if (hasError) {
        ok = false;
        errs[card.id] = ce;
      }
    });
    return { ok, errs };
  };



  const handleVerify = async () => {
    setLoading(true);
    try {
      const payload = {
        session_keys: sessionKeys
      };

      console.log("Verifying questions with session_keys:", sessionKeys);

      const res = await api.post("SaveQuestionBank", payload);

      console.log("Verify API Response:", res);

      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message || "Questions verified successfully!");
        // Clear localStorage cache
        localStorage.removeItem("generatedQuestions");
        localStorage.removeItem("sessionKeys");
        localStorage.removeItem("formData");
        localStorage.removeItem("questionFeedback");
        navigate(-1);
      } else {
        console.log("Unexpected verify response:", res?.data);
        showSnackbar("error", res?.data?.message || "Failed to verify questions");
      }
    } catch (error) {
      console.log("Verify error details:", error);
      console.log("Verify error response:", error?.response);
      console.log("Verify error message:", error?.message);
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while verifying questions");
    } finally {
      setLoading(false);
    }
  };



  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // When the AI response can't be processed the backend replies 503 + retry_after.
  // Show a friendly countdown and re-fire the exact same request automatically.
  // Only ONE regenerate is allowed (1 normal generate + 1 retry = 2 attempts total).
  const waitBeforeRetry = async (data, attempt, maxRetries) => {
    const seconds = Number(data?.retry_after) || 5;
    const sub = data?.subcategory ? ` on "${data.subcategory}"` : "";
    const totalAttempts = maxRetries + 1; // normal generate + retries
    const thisAttempt = attempt + 1; // 1-indexed position of the upcoming retry
    showSnackbar("warning", `Generation failed, regenerating in ${seconds} seconds...`);
    for (let s = seconds; s > 0; s -= 1) {
      setGenerationMessage(
        `Hit a snag${sub}. Regenerating in ${s}s… (attempt ${thisAttempt} of ${totalAttempts})`
      );
      await sleep(1000);
    }
    setGenerationMessage("Re-generating your questions…");
  };

  const generateQuestionBankWithRetry = async (payload, maxRetries = 1) => {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const res = await api.post("GenerateQuestionBank/", payload);
        const data = res?.data;
        // Some setups return 200 with status "Error" + retry_after instead of 503
        const softError =
          data && String(data.status).toLowerCase() === "error" && data.retry_after;
        if (softError && attempt < maxRetries) {
          attempt += 1;
          await waitBeforeRetry(data, attempt, maxRetries);
          continue;
        }
        return res;
      } catch (error) {
        const data = error?.response?.data;
        const retryable = error?.response?.status === 503 && data?.retry_after;
        if (retryable && attempt < maxRetries) {
          attempt += 1;
          await waitBeforeRetry(data, attempt, maxRetries);
          continue;
        }
        throw error;
      }
    }
  };

  // ---- Streaming generation (SSE) helpers ----

  // Absolute URL for the streaming endpoint, reusing the axios base URL so it
  // stays in sync with src/redux/api.js.
  const buildGenerateUrl = () => {
    const base = (api.defaults.baseURL || "").replace(/\/+$/, "");
    return `${base}/GenerateQuestionBank/`;
  };

  // Best-effort persistence so a refresh mid-stream keeps the progress so far.
  const persistGenerated = (questions, keys, formMeta) => {
    try {
      localStorage.setItem("generatedQuestions", JSON.stringify(questions));
      localStorage.setItem("sessionKeys", JSON.stringify(keys));
      if (formMeta) localStorage.setItem("formData", JSON.stringify(formMeta));
    } catch (storageError) {
      console.warn("localStorage quota exceeded. Storing in memory only.", storageError);
    }
  };

  // Mark the first matching pending card with a new status (handles duplicate
  // subcategory names by filling them in arrival order).
  const markStreamCard = (list, subcategory, status, extra = {}) => {
    let updated = false;
    return list.map((c) => {
      if (!updated && c.subcategory === subcategory && c.status === "pending") {
        updated = true;
        return { ...c, status, message: "", retry_after: null, ...extra };
      }
      return c;
    });
  };

  // Apply a batch_result to the first pending/streaming card with this subcategory.
  // Tracks received-question count and which batch index we're on for progress UI.
  const applyBatchToStreamCard = (list, chunk) => {
    let updated = false;
    return list.map((c) => {
      const inProgress = c.status === "pending" || c.status === "streaming";
      if (!updated && c.subcategory === chunk.subcategory && inProgress) {
        updated = true;
        return {
          ...c,
          status: "streaming",
          message: "",
          retry_after: null,
          received: (c.received || 0) + (chunk.questions?.length || 0),
          batches_done: Math.max(c.batches_done || 0, chunk.batch || 0),
          total_batches: chunk.total_batches || c.total_batches || 0,
        };
      }
      return c;
    });
  };

  // Finalize the first pending/streaming card with this subcategory (card_result is now
  // a lightweight "done" signal; questions are delivered via batch_result events).
  // The visual flow's card_result additionally carries `interpretation`; stash it on the
  // card so the UI can distinguish visual cards from standard ones.
  const finalizeStreamCard = (list, chunk) => {
    let updated = false;
    return list.map((c) => {
      const inProgress = c.status === "pending" || c.status === "streaming";
      if (!updated && c.subcategory === chunk.subcategory && inProgress) {
        updated = true;
        return {
          ...c,
          status: "done",
          message: "",
          retry_after: null,
          total_questions:
            chunk.total_questions ?? c.received ?? null,
          ...(chunk.interpretation ? { interpretation: chunk.interpretation } : {}),
        };
      }
      return c;
    });
  };

  // Handle one parsed SSE chunk (batch_result | card_result | card_error | done).
  const handleStreamChunk = (chunk, formMeta) => {
    if (!chunk || !chunk.type) return;
    if (chunk.type === "batch_result") {
      const incoming = chunk.questions || [];
      streamQuestionsRef.current = [...streamQuestionsRef.current, ...incoming];
      if (chunk.session_key) {
        streamKeysRef.current = [...new Set([...streamKeysRef.current, chunk.session_key])];
      }
      setGeneratedQuestions(streamQuestionsRef.current);
      setSessionKeys(streamKeysRef.current);
      persistGenerated(streamQuestionsRef.current, streamKeysRef.current, formMeta);
      setStreamCards((prev) => applyBatchToStreamCard(prev, chunk));
    } else if (chunk.type === "card_result") {
      // Card-done signal only — questions arrived via batch_result events.
      // Some older builds may still send `questions` here; if so, accept them defensively.
      if (Array.isArray(chunk.questions) && chunk.questions.length > 0) {
        streamQuestionsRef.current = [...streamQuestionsRef.current, ...chunk.questions];
        setGeneratedQuestions(streamQuestionsRef.current);
      }
      if (chunk.session_key) {
        streamKeysRef.current = [...new Set([...streamKeysRef.current, chunk.session_key])];
      }
      setSessionKeys(streamKeysRef.current);
      persistGenerated(streamQuestionsRef.current, streamKeysRef.current, formMeta);
      setStreamCards((prev) => finalizeStreamCard(prev, chunk));
      // Visual flow: backend refines the interpretation during generation and returns
      // it on the card_result. Update the global state so RegenerateVisualImage uses
      // the freshest interpretation, not the pre-generation preview.
      if (chunk.interpretation && typeof chunk.interpretation === "object") {
        setInterpretationData(chunk.interpretation);
      }
    } else if (chunk.type === "card_error") {
      setStreamCards((prev) =>
        markStreamCard(prev, chunk.subcategory, "error", {
          message: chunk.message || "AI response could not be processed.",
          retry_after: chunk.retry_after || null,
        })
      );
      showSnackbar("warning", `${chunk.subcategory}: ${chunk.message || "Generation failed"}`);
    } else if (chunk.type === "done") {
      if (Array.isArray(chunk.session_keys)) {
        // Merge (a single-card retry's `done` only reports that one key)
        streamKeysRef.current = [...new Set([...streamKeysRef.current, ...chunk.session_keys])];
        setSessionKeys(streamKeysRef.current);
        persistGenerated(streamQuestionsRef.current, streamKeysRef.current, formMeta);
      }
      streamDoneRef.current = true;
      setGenerationComplete(true);
    }
  };

  // Open the POST stream and consume it. Both the standard flow and the visual
  // (reference-image) flow return SSE. The non-streaming branch below is a safety
  // net for older backend builds that might still return a plain JSON body.
  const consumeQuestionStream = async (payload, { formMeta, isRetry = false } = {}) => {
    const token = localStorage.getItem("token");
    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const response = await fetch(buildGenerateUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: abortController.signal,
    });

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = "/";
      return;
    }
    if (!response.ok) {
      let backendMessage = "";
      try {
        const body = await response.text();
        try {
          const json = JSON.parse(body);
          const detail = json?.detail;
          backendMessage =
            json?.message ||
            json?.error ||
            (typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.map((d) => d?.msg).filter(Boolean).join(", ")
                : detail?.message) ||
            "";
        } catch {
          if (!body.trim().startsWith("<")) backendMessage = body;
        }
      } catch (readError) {
        console.log("Error reading generation error response:", readError);
      }
      throw new Error(backendMessage.trim() || `Generation failed (HTTP ${response.status})`);
    }

    const contentType = response.headers.get("content-type") || "";

    // Safety net for older backends that don't return SSE — visual flow used to
    // return a single JSON blob with `questions`, `session_keys`, and `interpretation`.
    if (!contentType.includes("text/event-stream") || !response.body) {
      const dataJson = await response.json();
      const questions = dataJson?.questions || [];
      const keys = dataJson?.session_keys || [];
      streamQuestionsRef.current = isRetry
        ? [...streamQuestionsRef.current, ...questions]
        : questions;
      streamKeysRef.current = [...new Set([...streamKeysRef.current, ...keys])];
      setGeneratedQuestions(streamQuestionsRef.current);
      setSessionKeys(streamKeysRef.current);
      persistGenerated(streamQuestionsRef.current, streamKeysRef.current, formMeta);
      if (dataJson?.interpretation && typeof dataJson.interpretation === "object") {
        setInterpretationData(dataJson.interpretation);
      }
      streamDoneRef.current = true;
      setGenerationComplete(true);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop(); // keep any incomplete trailing line
      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith("data:")) continue;
        const jsonStr = line.slice(5).trim();
        if (!jsonStr) continue;
        try {
          handleStreamChunk(JSON.parse(jsonStr), formMeta);
        } catch (parseErr) {
          console.warn("Skipping malformed SSE chunk:", jsonStr);
        }
      }
    }
  };

  // Re-stream a single failed card (backend handles single-card payloads identically).
  const retryCard = async (streamCard) => {
    const base = lastPayloadRef.current;
    if (!base) return;
    const subPayload = base.payload.subcategories.find(
      (s) => s.subcategory === streamCard.subcategory
    );
    if (!subPayload) return;

    setStreamCards((prev) =>
      prev.map((c) =>
        c.key === streamCard.key
          ? {
            ...c,
            status: "pending",
            message: "",
            retry_after: null,
            received: 0,
            batches_done: 0,
            total_batches: 0,
          }
          : c
      )
    );
    setGenerationComplete(false);
    streamDoneRef.current = false;
    setIsStreaming(true);
    try {
      await consumeQuestionStream(
        { ...base.payload, subcategories: [subPayload] },
        { formMeta: base.formMeta, isRetry: true }
      );
    } catch (error) {
      if (error?.name !== "AbortError") {
        setStreamCards((prev) =>
          prev.map((c) =>
            c.key === streamCard.key
              ? { ...c, status: "error", message: error?.message || "Retry failed" }
              : c
          )
        );
      }
    } finally {
      setIsStreaming(false);
      setGenerationComplete(true);
    }
  };

  const onSubmit = async (data) => {
    const { ok, errs } = validateCards();
    if (!ok) {
      setCardErrors(errs);
      showSnackbar("error", "Please fill all required fields in each configuration");
      return;
    }

    // Enforce the combined question limit across every configuration card
    const totalQuestionsCount = cards.reduce(
      (sum, c) => sum + (parseInt(c.questionsCount, 10) || 0),
      0
    );
    if (totalQuestionsCount > MAX_TOTAL_QUESTIONS) {
      showSnackbar(
        "error",
        `Total number of questions across all configurations must not exceed ${MAX_TOTAL_QUESTIONS} (currently ${totalQuestionsCount}).`
      );
      return;
    }

    // Build the reference image (base64) if one was uploaded — drives the visual flow.
    let imageBase64 = null;
    let imageMime = null;
    const hasImage = !!watch("referenceImage");
    if (hasImage) {
      try {
        if (interpretedImagePreview && interpretedImagePreview.startsWith("data:")) {
          const parts = interpretedImagePreview.split(",");
          imageBase64 = parts[1];
          const mimeMatch = parts[0].match(/data:(.*?);base64/);
          imageMime = mimeMatch ? mimeMatch[1] : "image/png";
        } else if (data.referenceImage) {
          imageBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(data.referenceImage);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (err) => reject(err);
          });
          imageMime = data.referenceImage.type || "image/png";
        }
      } catch (imgErr) {
        showSnackbar("error", "Failed to read the reference image.");
        return;
      }
    }

    // Each configuration card becomes one subcategory entry carrying its own settings.
    // Per the API contract, image_base64/image_mime and lab_tool_id live on the card.
    const subcategories = cards.map((card) => {
      const pair = card.subCategoryDescriptionPairs[0] || {};
      const labToolId = card.toolRequirement?.id ?? null;
      return {
        subcategory: pair.subCategory || "",
        description: pair.description || "",
        level: card.level || "",
        question_type: card.questionType || "",
        marks: card.giveMarks ? Number(card.giveMarks) : 0,
        blooms_levels: card.bloomsLevels || [],
        no_of_questions: card.questionsCount ? Number(card.questionsCount) : 0,
        lab_tool_id: labToolId,
        image_base64: null,
        image_mime: null,
        testcase_count: Number(card.testcase_count) || 0,
      };
    });

    // Visual flow is single-card; the uploaded image rides on the first subcategory card.
    if (imageBase64 && subcategories.length > 0) {
      subcategories[0].image_base64 = imageBase64;
      subcategories[0].image_mime = imageMime;
    }

    const payload = {
      category:
        data.category?.category_code ||
        data.category?.category_name ||
        data.category?.name ||
        "",
      subcategories,
      cognitive: null,
    };
    const formMeta = { program: data.program, category: data.category, cards };

    // ── Unified flow: both standard and visual (reference-image) generation now
    //    return SSE. Consume the stream and render per-card skeleton loaders. ──
    streamQuestionsRef.current = [];
    streamKeysRef.current = [];
    streamDoneRef.current = false;
    lastPayloadRef.current = { payload, formMeta };
    setGeneratedQuestions([]);
    setSessionKeys([]);
    setGenerationComplete(false);
    setStreamCards(
      subcategories.map((s, i) => ({
        key: `${s.subcategory || "card"}-${i}`,
        subcategory: s.subcategory,
        status: "pending",
        message: "",
        retry_after: null,
      }))
    );
    setIsStreaming(true);
    persistGenerated([], [], formMeta); // store form context early

    let failureMessage = "";
    try {
      await consumeQuestionStream(payload, { formMeta });
      if (streamDoneRef.current) {
        showSnackbar("success", "Generation complete");
      }
    } catch (error) {
      if (error?.name === "AbortError") return; // navigated away — ignore
      console.log("Streaming error:", error);
      failureMessage = error?.message || "";
      // Short toast; the full reason is shown on the section card in Generated Questions
      showSnackbar("error", "Failed to generate questions");
    } finally {
      // Any card still pending after the stream ends (e.g. dropped connection) is interrupted.
      if (!streamDoneRef.current) {
        setStreamCards((prev) =>
          prev.map((c) =>
            c.status === "pending"
              ? { ...c, status: "error", message: failureMessage || "Generation was interrupted. Please retry." }
              : c
          )
        );
      }
      setIsStreaming(false);
      // Enable submit for whatever succeeded, even on a dropped connection.
      setGenerationComplete(true);
    }
  };

  const handleInterpretImage = async (data) => {
    setIsGenerating(true);
    setGenerationTitle("AI is interpreting your image");
    setGenerationMessage("Analyzing the diagram and extracting key details…");
    try {
      // Interpretation uses the first configuration card for context
      const firstCard = cards[0] || {};
      const firstPair = firstCard.subCategoryDescriptionPairs?.[0] || {};
      const subcategory = firstPair.subCategory || "";
      const subcategoryDesc = firstPair.description || "";

      const file = data.referenceImage;
      if (!file) {
        showSnackbar("error", "Please upload a reference image first.");
        return;
      }

      const pureBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64String = reader.result.split(",")[1];
          resolve(base64String);
        };
        reader.onerror = (err) => reject(err);
      });

      const payload = {
        image_base64: pureBase64,
        image_mime: file.type || "image/png",
        category: data.category?.category_name || data.category?.name || null,
        subcategory: subcategory,
        description: subcategoryDesc,
        level: firstCard.level || null,
        question_type: firstCard.questionType || null,
        no_of_questions: firstCard.questionsCount ? Number(firstCard.questionsCount) : 5,
      };

      console.log("Calling InterpretVisualImage with payload:", payload);
      const res = await api.post("InterpretVisualImage", payload);
      console.log("InterpretVisualImage Response:", res.data);

      if (res.data?.status === "success" || res.data?.interpretation) {
        const interpretation = res.data.interpretation || {};
        const descriptionText = interpretation.description || "";
        setManualDescription(descriptionText);
        setInterpretationData(interpretation);

        if (res.data.image_base64) {
          const mimeType = res.data.image_mime || "image/png";
          setInterpretedImagePreview(`data:${mimeType};base64,${res.data.image_base64}`);
        }

        setShowManualCreateCard(true);
        showSnackbar("success", "Image interpreted successfully!");
      } else {
        showSnackbar("error", "Interpretation failed: Invalid response structure");
      }
    } catch (error) {
      console.error("InterpretVisualImage Error:", error);
      showSnackbar("error", error?.response?.data?.message || "Failed to interpret image");
    } finally {
      setIsGenerating(false);
      setGenerationMessage("");
    }
  };

  const handleRegenerateImage = async () => {
    setIsGenerating(true);
    setGenerationTitle("AI is regenerating your image");
    setGenerationMessage("Recreating the diagram from your description…");
    try {
      const payload = {
        interpretation: {
          subject: interpretationData?.subject || "",
          topic: interpretationData?.topic || "",
          sub_topic: interpretationData?.sub_topic || "",
          detected_elements: interpretationData?.detected_elements || [],
          pedagogical_focus: interpretationData?.pedagogical_focus || "",
          description: manualDescription,
          curriculum_level: interpretationData?.curriculum_level || "",
          confidence: interpretationData?.confidence || 0.91
        }
      };

      console.log("Calling RegenerateVisualImage with payload:", payload);
      const res = await api.post("RegenerateVisualImage", payload);
      console.log("RegenerateVisualImage Response:", res.data);

      if (res.data?.status === "success" && res.data.image_base64) {
        const mimeType = res.data.image_mime || "image/png";
        setInterpretedImagePreview(`data:${mimeType};base64,${res.data.image_base64}`);
        showSnackbar("success", "Image regenerated successfully!");
      } else {
        showSnackbar("error", res.data?.message || "Failed to regenerate image.");
      }
    } catch (error) {
      console.error("RegenerateVisualImage Error:", error);
      showSnackbar("error", error?.response?.data?.message || "Failed to regenerate image");
    } finally {
      setIsGenerating(false);
      setGenerationMessage("");
    }
  };

  const getFileIcon = (file) => {
    if (!file) return null;

    const extension = file.name.split(".").pop()?.toLowerCase();

    if (extension === "pdf") return pdfIcon;
    if (extension === "doc" || extension === "docx") return docIcon;

    return null;
  };

  const handleClearAll = () => {
    reset();
    localStorage.removeItem("generatedQuestions");
    localStorage.removeItem("sessionKeys");
    localStorage.removeItem("formData");
    localStorage.removeItem("questionFeedback");
    setGeneratedQuestions([]);
    setSessionKeys([]);
    setQuestionFeedback({});
    setCards([createEmptyCard()]);
    setCardErrors({});
    setTopicChips([]);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setInterpretedImagePreview(null);
    setShowManualCreateCard(false);
    setInterpretationData(null);
    setStreamCards([]);
    setGenerationComplete(false);
    streamQuestionsRef.current = [];
    streamKeysRef.current = [];
  };



  const commonTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "#d1d1d1" },
      "&.Mui-focused fieldset": {
        borderColor: "#1c97e3",
      },
    },
  };

  const questionNumberBadgeStyle = {
    flexShrink: 0,
    minWidth: "30px",
    height: "30px",
    px: 0.5,
    borderRadius: "9px",
    background: "linear-gradient(135deg, #1c97e3 0%, #03438A 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 700,
    boxShadow: "0 2px 6px rgba(3,67,138,0.25)",
  };

  const noSpinnerNumberStyle = {
    ...commonTextFieldStyle,
    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  // Live totals computed across every configuration card
  const totalQuestions = cards.reduce(
    (sum, c) => sum + (parseInt(c.questionsCount, 10) || 0),
    0
  );
  const exceedsQuestionLimit = totalQuestions > MAX_TOTAL_QUESTIONS;

  // While the page is loading (initial fetches, save, verify) or generating questions,
  // lock every form field so the user can't change inputs mid-request.
  const formDisabled = loading || isStreaming || isGenerating;

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {loading && <OverAllLoader message="Please wait..." />}
      <LoadingOverlay
        open={isGenerating}
        title={generationTitle || "AI is crafting your questions"}
        message={generationMessage}
        note="This can take up to 5 minutes. Please don't refresh or close this window."
      />
      <Box>
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "40px",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
          </Grid>

          {/* Form Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                pb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  backgroundColor: "#1c97e3",
                  p: 0.5,
                  borderRadius: "10px",
                }}
              >
                <CategoryIcon sx={{ color: "white", fontSize: "35px" }} />
              </Grid>
              <Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {edit === true ? "Update" : "Add"} Setup Question Details
                </Typography>
              </Grid>
            </Grid>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 3,
                  p: 3,
                }}
              >
                {/* ===== Common Details (shared across all configurations) ===== */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CategoryIcon sx={{ color: "#1c97e3", fontSize: "20px" }} />
                  <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c97e3" }}>
                    Common Details
                  </Typography>
                </Box>

                <Box
                  sx={{
                    border: "1px solid #e3eaf2",
                    borderRadius: "14px",
                    p: 3,
                    backgroundColor: "#fbfdff",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: 3,
                  }}
                >
                  {/* Program & Subject */}
                  <Grid sx={{ display: "flex", gap: 5 }}>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        {getLabel("PROGRAMS")} <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="program"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={programs}
                            disabled={formDisabled}
                            getOptionLabel={(option) => {
                              if (!option) return "";
                              const label = option.display_name || option.program_name || "";
                              return option.program_code
                                ? `[${option.program_code}] ${label}`
                                : label;
                            }}
                            isOptionEqualToValue={(o, v) => o?.program_id === v?.program_id}
                            value={field.value}
                            onChange={(_, value) => {
                              field.onChange(value);
                              // Reset Subject + all configurations when Program changes
                              setValue("category", null);
                              setCards([createEmptyCard()]);
                              setCardErrors({});
                              setTopicChips([]);
                              fetchCategoriesByProgram(value?.program_id);
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.program_id}>
                                {option.program_code ? `[${option.program_code}] ` : ""}
                                {option.display_name || option.program_name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel("PROGRAMS")}`}
                                error={!!errors.program}
                                helperText={errors.program?.message}
                                sx={commonTextFieldStyle}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid sx={{ width: "100%" }}>
                      <Typography>
                        {getLabel("SUBJECT")} <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={categories}
                            disabled={!watch("program") || formDisabled}
                            getOptionLabel={(option) => {
                              if (!option) return "";
                              const label = option.category_name || option.name || "";
                              return option.category_code
                                ? `[${option.category_code}] ${label}`
                                : label;
                            }}
                            isOptionEqualToValue={(option, value) =>
                              (option?.category_id ?? option?.id) ===
                              (value?.category_id ?? value?.id)
                            }
                            value={field.value}
                            onChange={(_, value) => {
                              field.onChange(value);
                              // Reset Subject Topics in every card when subject changes
                              setCards((prev) =>
                                prev.map((c) => ({
                                  ...c,
                                  subCategoryDescriptionPairs: [
                                    { id: nextId(), subCategory: "", description: "" },
                                  ],
                                }))
                              );
                              setCardErrors({});
                              setTopicChips([]);
                              const cid = value?.category_id ?? value?.id;
                              if (cid) {
                                getSubcategories(cid);
                              }
                            }}
                            onBlur={field.onBlur}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={option.category_id ?? option.id}
                              >
                                {option.category_code ? `[${option.category_code}] ` : ""}
                                {option.category_name || option.name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={watch("program") ? `Select ${getLabel("SUBJECT")}` : `Select ${getLabel("PROGRAMS")} first`}
                                error={!!errors.category}
                                helperText={errors.category?.message}
                                sx={commonTextFieldStyle}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Question Creation Through Reference Image.
                      When checked, the per-card "Tool / Lab Requirement" field is
                      replaced by the Reference Image upload (see Configuration cards). */}
                  <Grid sx={{ display: "flex", gap: 5 }}>
                    <Grid sx={{ width: "100%" }}>
                      <FormControlLabel
                        disabled={formDisabled}
                        control={
                          <Checkbox
                            disabled={formDisabled}
                            checked={createThroughReferenceImage}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCreateThroughReferenceImage(checked);
                              // Turning the mode off clears any uploaded image + its derived state
                              if (!checked) {
                                setValue("referenceImage", null);
                                if (imagePreview) URL.revokeObjectURL(imagePreview);
                                setImagePreview(null);
                                setInterpretedImagePreview(null);
                                setShowManualCreateCard(false);
                                setInterpretationData(null);
                              }
                            }}
                            sx={{
                              color: "#1c97e3",
                              "&.Mui-checked": { color: "#1c97e3" },
                            }}
                          />
                        }
                        label="Question Creation Through Reference Image"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "15px", color: "#333" } }}
                      />
                    </Grid>
                    {createThroughReferenceImage &&
                      <Grid sx={{ width: "100%" }}>
                        <Typography>Reference Image</Typography>
                        <Controller
                          name="referenceImage"
                          control={control}
                          render={({ field }) => (
                            <Box>
                              <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                sx={{
                                  height: "56px",
                                  justifyContent: "flex-start",
                                  textTransform: "none",
                                  color: field.value ? "#333" : "#9e9e9e",
                                  borderColor: errors.referenceImage ? "#d32f2f" : "white",
                                  borderWidth: "1px",
                                  borderStyle: "solid",
                                  paddingLeft: "14px",
                                  gap: 1.5,
                                  backgroundColor: "white",
                                  "&:hover": { borderColor: "#d1d1d1" },
                                }}
                              >
                                <AddPhotoAlternateIcon sx={{ color: field.value ? "#1c97e3" : "#9e9e9e", fontSize: "20px" }} />
                                <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "15px", fontWeight: 400 }}>
                                  {field.value ? field.value.name : "Reference Image (Max 5MB)"}
                                </Typography>
                                <input
                                  type="file"
                                  hidden
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0] || null;

                                    field.onChange(file);

                                    if (imagePreview) {
                                      URL.revokeObjectURL(imagePreview);
                                      setImagePreview(null);
                                    }

                                    if (!file) {
                                      setInterpretedImagePreview(null);
                                      e.target.value = "";
                                      return;
                                    }

                                    if (file.type.startsWith("image/")) {
                                      const previewUrl = URL.createObjectURL(file);

                                      setImagePreview(previewUrl);
                                      setInterpretedImagePreview(previewUrl);
                                    } else {
                                      // PDF / DOC / DOCX
                                      setImagePreview(null);
                                      setInterpretedImagePreview(null);
                                    }

                                    e.target.value = "";
                                  }}
                                />
                              </Button>
                              {errors.referenceImage && (
                                <Typography sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: "3px", ml: "14px", fontWeight: 400 }}>
                                  {errors.referenceImage.message}
                                </Typography>
                              )}
                              {field.value && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 1.5,
                                    border: "1px solid #d1d1d1",
                                    borderRadius: "8px",
                                    backgroundColor: "#fafafa",
                                  }}
                                >
                                  {imagePreview ? (
                                    // Image preview
                                    <Box
                                      component="img"
                                      src={imagePreview}
                                      alt="Preview"
                                      sx={{
                                        width: "100px",
                                        height: "100px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        border: "1px solid #d1d1d1",
                                      }}
                                    />
                                  ) : (
                                    // PDF / DOC / DOCX icon
                                    <Box
                                      component="img"
                                      src={getFileIcon(field.value)}
                                      alt="File"
                                      sx={{
                                        width: "70px",
                                        height: "70px",
                                        objectFit: "contain",
                                      }}
                                    />
                                  )}

                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {field.value.name}
                                    </Typography>

                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        color: "#8a97a8",
                                        mt: 0.5,
                                      }}
                                    >
                                      {(field.value.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                  </Box>

                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      field.onChange(null);

                                      if (imagePreview) {
                                        URL.revokeObjectURL(imagePreview);
                                      }

                                      setImagePreview(null);
                                      setInterpretedImagePreview(null);
                                    }}
                                    sx={{
                                      color: "#d32f2f",
                                      backgroundColor: "#ffebee",
                                      "&:hover": {
                                        backgroundColor: "#ffcdd2",
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          )}
                        />
                      </Grid>
                    }
                  </Grid>
                </Box>

                {/* ===== Question Configurations (duplicatable cards) ===== */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <TuneIcon sx={{ color: "#1c97e3", fontSize: "20px" }} />
                  <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1c97e3" }}>
                    Question Configurations
                  </Typography>
                  <Chip
                    label={cards.length}
                    size="small"
                    sx={{ backgroundColor: "#e4eefb", color: "#1c97e3", fontWeight: 700, height: "20px" }}
                  />
                </Box>

                {cards.map((card, cardIndex) => {
                  const ce = getCardErr(card.id);
                  return (
                    <Box
                      key={card.id}
                      sx={{
                        position: "relative",
                        border: "1px solid #d8e2ec",
                        borderRadius: "14px",
                        backgroundColor: "white",
                        boxShadow: "0px 3px 10px rgba(28, 151, 227, 0.06)",
                        display: "flex",
                        flexDirection: "column",
                        rowGap: 3,
                        p: 3,
                      }}
                    >
                      {/* Card header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "1px solid #eef2f7",
                          pb: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: "26px",
                              height: "26px",
                              borderRadius: "8px",
                              backgroundColor: "#1c97e3",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "13px",
                              fontWeight: 700,
                            }}
                          >
                            {cardIndex + 1}
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#37474f" }}>
                            Configuration {cardIndex + 1}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {!createThroughReferenceImage && (
                            <Tooltip
                              title={
                                cards.length >= MAX_CONFIGURATIONS
                                  ? `Maximum ${MAX_CONFIGURATIONS} question configurations are allowed.`
                                  : "Add configuration"
                              }
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateCard(card.id)}
                                  disabled={cards.length >= MAX_CONFIGURATIONS}
                                  sx={{
                                    color: "#1c97e3",
                                    backgroundColor: "#e4eefb",
                                    "&:hover": { backgroundColor: "#d2e4fb" },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {cards.length > 1 && (
                            <Tooltip
                              title="Remove this configuration"
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() => removeCard(card.id)}
                                sx={{
                                  color: "#d32f2f",
                                  backgroundColor: "#ffebee",
                                  "&:hover": { backgroundColor: "#ffcdd2" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Subject Topics + Description (dynamic pairs) */}
                      {card.subCategoryDescriptionPairs.map((pair) => {
                        const pairErr = (ce.pairs || {})[pair.id] || {};
                        return (
                          <Grid key={pair.id} sx={{ display: "flex", gap: 5, alignItems: "center" }}>
                            <Grid sx={{ flex: 1 }}>
                              <Typography sx={{ mb: 1.5 }}>  {getLabel("SUBJECT")} Topics <span style={{ color: "red" }}>*</span></Typography>
                              <TopicAutocomplete
                                disabled={!watch("category") || formDisabled}
                                options={topicChips
                                  .map((option) => option.name)
                                  .filter(
                                    (option) =>
                                      !card.subCategoryDescriptionPairs
                                        .filter((p) => p.id !== pair.id)
                                        .some((p) => p.subCategory === option)
                                  )}
                                value={pair.subCategory}
                                onCommit={(newValue) => updatePair(card.id, pair.id, "subCategory", newValue)}
                                placeholder={`Select or type ${getLabel("SUBJECT")} topic`}
                                error={!!pairErr.subCategory}
                                helperText={pairErr.subCategory || ""}
                                sx={commonTextFieldStyle}
                              />
                            </Grid>
                            <Grid sx={{ flex: 1 }}>
                              <Typography sx={{ mb: 1.5 }}>Description</Typography>
                              <CommitTextField
                                value={pair.description}
                                onCommit={(newValue) => updatePair(card.id, pair.id, "description", newValue)}
                                placeholder="Enter description..."
                                fullWidth
                                disabled={formDisabled}
                                error={!!pairErr.description}
                                helperText={pairErr.description || ""}
                                sx={commonTextFieldStyle}
                              />
                            </Grid>
                          </Grid>
                        );
                      })}

                      {ce.general && (
                        <Typography sx={{ color: "#d32f2f", fontSize: "0.75rem", mt: "-12px", ml: "2px" }}>
                          {ce.general}
                        </Typography>
                      )}

                      {/* Level & Question Type */}
                      <Grid sx={{ display: "flex", gap: 5 }}>
                        <Grid sx={{ width: "100%" }}>
                          <Typography>
                            Level <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <Autocomplete
                            options={levels.map((l) => l.name)}
                            disabled={formDisabled}
                            value={card.level || null}
                            onChange={(_, value) => updateCardField(card.id, "level", value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select level"
                                error={!!ce.level}
                                helperText={ce.level || ""}
                                sx={commonTextFieldStyle}
                              />
                            )}
                          />
                        </Grid>
                        <Grid sx={{ width: "100%" }}>
                          <Typography>Question Type <span style={{ color: "red" }}>*</span></Typography>
                          <Autocomplete
                            options={questionTypes}
                            disabled={formDisabled}
                            value={card.questionType || null}
                            onChange={(_, value) => updateCardField(card.id, "questionType", value || "")}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select question type"
                                error={!!ce.questionType}
                                helperText={ce.questionType || ""}
                                sx={commonTextFieldStyle}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Grid sx={{ display: "flex", gap: 5 }}>
                        <Grid sx={{ width: "100%" }}>
                          <Typography>No. Of Questions <span style={{ color: "red" }}>*</span></Typography>
                          <CommitTextField
                            type="number"
                            placeholder="25"
                            fullWidth
                            disabled={formDisabled}
                            inputProps={{ min: 1, max: MAX_TOTAL_QUESTIONS }}
                            value={card.questionsCount}
                            onCommit={(newValue) => updateCardField(card.id, "questionsCount", newValue)}
                            error={!!ce.questionsCount}
                            helperText={ce.questionsCount || ""}
                            sx={noSpinnerNumberStyle}
                          />
                        </Grid>
                        <Grid sx={{ width: "100%" }}>
                          <Typography>
                            Marks Per Question <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <CommitTextField
                            type="number"
                            placeholder="1-20"
                            fullWidth
                            disabled={formDisabled}
                            inputProps={{ min: 1, max: 20 }}
                            value={card.giveMarks}
                            onCommit={(newValue) => updateCardField(card.id, "giveMarks", newValue)}
                            error={!!ce.giveMarks}
                            helperText={ce.giveMarks || ""}
                            sx={noSpinnerNumberStyle}
                          />
                        </Grid>
                      </Grid>

                      {/* Bloom's Taxonomy Level (multi-select) & Tool / Lab Requirement */}
                      <Grid sx={{ display: "flex", gap: 5 }}>
                        <Grid sx={{ width: "100%" }}>
                          <Typography>
                            Bloom's Taxonomy Level <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <Autocomplete
                            multiple
                            disableCloseOnSelect
                            disabled={formDisabled}
                            options={bloomsOptions.map((b) => b.blooms_name)}
                            value={card.bloomsLevels || []}
                            onChange={(_, value) => updateCardField(card.id, "bloomsLevels", value)}
                            renderOption={(props, option, { selected }) => (
                              <li {...props} key={option}>
                                <Checkbox checked={selected} readOnly sx={{ mr: 1 }} />
                                {option}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select one or more levels"
                                error={!!ce.bloomsLevels}
                                helperText={ce.bloomsLevels || ""}
                                sx={commonTextFieldStyle}
                              />
                            )}
                          />
                        </Grid>
                        {
                          !createThroughReferenceImage ?
                            <Grid sx={{ width: "100%" }}>
                              <Typography>Tool / Lab Requirement</Typography>
                              <Autocomplete
                                options={labTools}
                                disabled={formDisabled}
                                getOptionLabel={(option) => option?.tool_name || ""}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                value={
                                  card.toolRequirement && typeof card.toolRequirement === "object"
                                    ? card.toolRequirement
                                    : null
                                }
                                onChange={(_, value) => updateCardField(card.id, "toolRequirement", value)}
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id}>
                                    {option.tool_name}
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select tool or lab requirement"
                                    sx={commonTextFieldStyle}
                                  />
                                )}
                              />
                            </Grid> : (
                              <Grid sx={{ width: "100%" }}></Grid>
                            )
                        }
                      </Grid>

                      {/* No. Of Questions & Marks Per Question */}
                      <Grid sx={{ display: "flex", gap: 5 }}>
                        {card.questionType === "Programming-based" && (
                          <Grid sx={{ width: "100%" }}>
                            <Typography>
                              Test Case Count <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <CommitTextField
                              type="number"
                              placeholder="e.g. 5"
                              fullWidth
                              disabled={formDisabled}
                              inputProps={{ min: 1 }}
                              value={card.testcase_count || ""}
                              onCommit={(newValue) => updateCardField(card.id, "testcase_count", newValue)}
                              error={!!ce.testcase_count}
                              helperText={ce.testcase_count || ""}
                              sx={noSpinnerNumberStyle}
                            />
                          </Grid>
                        )}
                        <Grid sx={{ width: "100%" }}></Grid>
                      </Grid>
                    </Box>
                  );
                })}

                {/* Totals summary across all configurations */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    p: 2,
                    borderRadius: "12px",
                    border: exceedsQuestionLimit ? "1px solid #ef9a9a" : "1px solid #cfe5fb",
                    backgroundColor: exceedsQuestionLimit ? "#fff5f5" : "#f3f9ff",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#37474f" }}>
                    Summary across {cards.length} configuration{cards.length > 1 ? "s" : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "14px", color: "#546e7a" }}>
                      Total Questions:{" "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: exceedsQuestionLimit ? "#d32f2f" : "#1c97e3",
                        }}
                      >
                        {totalQuestions} / {MAX_TOTAL_QUESTIONS}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
                {exceedsQuestionLimit && (
                  <Typography sx={{ color: "#d32f2f", fontSize: "0.8rem", mt: "-12px" }}>
                    Total number of questions across all configurations must not exceed{" "}
                    {MAX_TOTAL_QUESTIONS}. Reduce the count in one or more cards.
                  </Typography>
                )}



                {/* Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleClearAll}
                    sx={{ textTransform: "none", borderRadius: "12px", px: 3, fontWeight: 600 }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || isGenerating || isStreaming || exceedsQuestionLimit}
                    onClick={handleSubmit(watch("referenceImage") ? handleInterpretImage : onSubmit)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      fontWeight: 600,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <AutoAwesomeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {watch("referenceImage") ? "Generated Image" : "Generate Questions"}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>

          {/* Manual Create Section Card */}
          {watch("referenceImage") && showManualCreateCard && (
            <Box
              sx={{
                mt: 4,
                border: "1px solid #d1d1d1",
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                backgroundColor: "white",
                p: 4,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  borderBottom: "1px solid #E0E5F2",
                  pb: 2,
                  mb: 3,
                }}
              >
                <AddPhotoAlternateIcon sx={{ color: "#1c97e3", fontSize: "28px" }} />
                <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1c97e3" }}>
                  Manual Image Reference Details
                </Typography>
              </Box>

              {/* Uploaded Image Preview (Lightly Big) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 4,
                  backgroundColor: "#fcfcfc",
                  border: "1px dashed #cfd8dc",
                  borderRadius: "12px",
                  p: 3,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {interpretedImagePreview ? (
                  <Box
                    component="img"
                    src={interpretedImagePreview}
                    alt="Manual Upload Reference"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "380px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0px 6px 16px rgba(0,0,0,0.06)",
                    }}
                  />
                ) : (
                  <Typography sx={{ color: "#999", py: 4 }}>
                    No reference image uploaded.
                  </Typography>
                )}
              </Box>

              {/* Description field under the image */}
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#37474f", mb: 1.5 }}>
                  Automatically Bound Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Enter dynamic circuit or template description here..."
                  sx={{
                    ...commonTextFieldStyle,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                />
              </Box>

              {/* Action Buttons inside Card */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 4,
                  borderTop: "1px solid #E0E5F2",
                  pt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowManualCreateCard(false)}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 3, fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRegenerateImage}
                  disabled={loading}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 3, fontWeight: 600, display: "flex", gap: 1 }}
                >
                  <SyncIcon fontSize="small" />
                  Regenerate Image
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || exceedsQuestionLimit}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600, display: "flex", gap: 1 }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                  Generate Questions
                </Button>
              </Box>
            </Box>
          )}



          {/* Generated Questions Section */}
          {(generatedQuestions.length > 0 || streamCards.length > 0) && (
            <Box
              sx={{
                mt: 4,
                border: "1px solid #d1d1d1",
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                backgroundColor: "white",
                p: 4,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E0E5F2", pb: 2, mb: 3 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <DescriptionIcon sx={{ color: "#03438A" }} />
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: "#03438A" }}>Generated Questions</Typography>
                </Box>
                {streamCards.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {isStreaming && <CircularProgress size={16} sx={{ color: "#1c97e3" }} />}
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: isStreaming ? "#1c97e3" : "#2e7d32" }}>
                      {streamCards.filter((c) => c.status === "done").length} of {streamCards.length}
                      {isStreaming ? " sections generating…" : " sections complete"}
                    </Typography>
                  </Box>
                )}
              </Box>



              {generatedQuestions.map((q, index) => {
                const isRejected = q.status === "rejected";
                const hasUserFeedback = !!questionFeedback[q.quiz_id];
                const showFeedbackBlock = isRejected || hasUserFeedback;

                let feedbackTitle = "Feedback Provided";
                let feedbackIssue = "";
                let feedbackDetails = "";

                if (hasUserFeedback) {
                  feedbackTitle = "Feedback Provided";
                  feedbackIssue = issues.find(i => i.value === questionFeedback[q.quiz_id].issue)?.label || questionFeedback[q.quiz_id].issue;
                  feedbackDetails = questionFeedback[q.quiz_id].description;
                } else if (isRejected) {
                  feedbackTitle = "Question Rejected";
                  feedbackIssue = "Rejected";
                  feedbackDetails = q.feedback || "Question has been rejected by the system.";
                }

                const answerKey = q.quiz_id ?? index;
                const isEditing = !!editingQuestions[answerKey];

                return (
                  <Box
                    key={q.quiz_id ?? index}
                    sx={{
                      mb: 3,
                      p: { xs: 2.5, md: 3 },
                      borderRadius: "16px",
                      border: "1px solid #eef2f7",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
                      transition: "box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease",
                      "&:hover": {
                        boxShadow: "0 10px 28px rgba(28,151,227,0.10)",
                        borderColor: "#d8e9fb",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {q.chips && q.chips.map((chip, idx) => (
                          <Chip key={idx} label={chip} size="small" sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: idx === 3 ? "#FFE8D6" : "#e4eefb", color: idx === 3 ? "#D97B29" : "#1c97e3", borderRadius: "12px", letterSpacing: "0.5px" }} />
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {q.blooms_level && (
                          <Chip
                            label={q.blooms_level}
                            size="small"
                            icon={<PsychologyOutlinedIcon sx={{ fontSize: "15px !important", color: "#5e35b1 !important" }} />}
                            sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: "#ede7f6", color: "#5e35b1", borderRadius: "12px", letterSpacing: "0.5px" }}
                          />
                        )}
                        {(q.marks !== undefined && q.marks !== null) && (
                          <Chip
                            label={`${q.marks} ${Number(q.marks) === 1 ? "Mark" : "Marks"}`}
                            size="small"
                            icon={<MilitaryTechOutlinedIcon sx={{ fontSize: "15px !important", color: "#e65100 !important" }} />}
                            sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: "#fff3e0", color: "#e65100", borderRadius: "12px", letterSpacing: "0.5px" }}
                          />
                        )}
                        {(q.status !== "rejected" && !hasUserFeedback) && (
                          <IconButton size="small" onClick={() => { setFeedbackOpen(true); setCurrentQuestion(q); }}><ThumbDownAltOutlinedIcon sx={{ color: "#8E9EBC" }} /></IconButton>
                        )}
                      </Box>
                    </Box>
                    {isEditing ? (
                      <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "flex-start" }}>
                        <Box sx={questionNumberBadgeStyle}>{index + 1}</Box>
                        <TextField
                          fullWidth
                          multiline
                          value={q.question || ""}
                          onChange={(e) => updateQuestionField(index, "question", e.target.value)}
                          placeholder="Enter the question..."
                          sx={commonTextFieldStyle}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "flex-start" }}>
                        <Box sx={questionNumberBadgeStyle}>{index + 1}</Box>
                        <Box sx={{ flex: 1, pt: "2px" }}>
                          <MarkdownQuestion text={q.question} />
                        </Box>
                      </Box>
                    )}

                    {/* Image rendering if image_url exists */}
                    {q.image_url && (
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          p: 2,
                          backgroundColor: "#fcfcfc",
                          maxWidth: "100%",
                        }}
                      >
                        <Box
                          component="img"
                          src={q.image_url}
                          alt="Generated Diagram / Circuit"
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "350px",
                            objectFit: "contain",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>
                    )}

                    {/* Feedback Indicator */}
                    {showFeedbackBlock && (
                      <Box sx={{
                        mb: 3,
                        p: 3,
                        backgroundColor: "#fef3c7",
                        borderRadius: "12px",
                        border: "1px solid #fbbf24",
                        position: "relative",
                        overflow: "hidden"
                      }}>
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2
                        }}>
                          <Box sx={{
                            backgroundColor: "#fbbf24",
                            borderRadius: "50%",
                            p: 0.8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                            <ThumbDownAltOutlinedIcon sx={{ color: "#fff", fontSize: "16px" }} />
                          </Box>
                          <Typography sx={{
                            fontWeight: 700,
                            fontSize: "14px",
                            color: "#92400e"
                          }}>
                            {feedbackTitle}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                          <Typography sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            color: "#b45309",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Issue:
                          </Typography>
                          <Chip
                            label={feedbackIssue}
                            size="small"
                            sx={{
                              backgroundColor: "#fff",
                              color: "#92400e",
                              fontWeight: 600,
                              fontSize: "11px",
                              height: "24px",
                              border: "1px solid #fbbf24"
                            }}
                          />
                        </Box>
                        {feedbackDetails && (
                          <Box>
                            <Typography sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              color: "#b45309",
                              mb: 0.5,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}>
                              Details:
                            </Typography>
                            <Typography sx={{
                              fontSize: "13px",
                              color: "#78350f",
                              lineHeight: 1.5
                            }}>
                              {feedbackDetails}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {(() => {
                      // Detect the correct MCQ option from an answer like "(b) O(1)"
                      const answerText = q.answer || "";
                      const labelMatch = answerText.match(/^\s*\(?\s*([A-Za-z])\s*[).]/);
                      const correctLabel = labelMatch ? labelMatch[1].toUpperCase() : null;
                      const hasOptions = q.options && q.options.length > 0;
                      const isAnswerOpen = !!expandedAnswers[answerKey];
                      return (
                        <>
                          {hasOptions && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              {q.options.map((opt, oIdx) => {
                                // Reveal the correct option only once the answer is expanded
                                const isCorrect =
                                  isAnswerOpen &&
                                  correctLabel &&
                                  opt.label?.toUpperCase() === correctLabel;
                                return (
                                  <Box
                                    key={oIdx}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      p: 1.5,
                                      backgroundColor: isCorrect ? "#e8f5e9" : "#f6f8fb",
                                      borderRadius: "8px",
                                      border: isCorrect ? "1px solid #a5d6a7" : "1px solid #f1f3f6",
                                      transition: "background-color 0.3s ease, border-color 0.3s ease",
                                    }}
                                  >
                                    {isCorrect ? (
                                      <CheckCircleIcon sx={{ fontSize: "20px", color: "#2e7d32", mr: 1.5 }} />
                                    ) : (
                                      <RadioButtonUncheckedIcon sx={{ fontSize: "20px", color: "#cfd8dc", mr: 1.5 }} />
                                    )}
                                    <Typography sx={{ fontSize: "14px", fontWeight: 600, mr: 1, color: isCorrect ? "#2e7d32" : "#8f9ebc" }}>{opt.label}.</Typography>
                                    {isEditing ? (
                                      <TextField
                                        variant="standard"
                                        fullWidth
                                        value={opt.text || ""}
                                        onChange={(e) => updateOptionText(index, oIdx, e.target.value)}
                                        placeholder={`Option ${opt.label}`}
                                        InputProps={{ disableUnderline: false }}
                                        sx={{
                                          "& .MuiInput-input": { fontSize: "14px", color: "#546e7a", fontWeight: 500, py: 0.25 },
                                        }}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: "14px", color: isCorrect ? "#1b5e20" : "#546e7a", fontWeight: 500 }}>{opt.text}</Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {/* Per-question action pills */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: hasOptions ? 3 : 1, flexWrap: "wrap" }}>
                            {!isEditing && answerText && (
                              <Box
                                onClick={() => toggleAnswer(answerKey)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  cursor: "pointer",
                                  userSelect: "none",
                                  color: "#2e7d32",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  px: 1.5,
                                  py: 0.75,
                                  borderRadius: "8px",
                                  border: "1px solid #a5d6a7",
                                  backgroundColor: "#f1f8f2",
                                  transition: "background-color 0.2s ease",
                                  "&:hover": { backgroundColor: "#e3f2e5" },
                                }}
                              >
                                {isAnswerOpen ? (
                                  <VisibilityOffOutlinedIcon sx={{ fontSize: "18px" }} />
                                ) : (
                                  <VisibilityOutlinedIcon sx={{ fontSize: "18px" }} />
                                )}
                                {isAnswerOpen ? "Hide Answer" : "View Answer"}
                                <ExpandMoreIcon
                                  sx={{
                                    fontSize: "18px",
                                    transition: "transform 0.3s ease",
                                    transform: isAnswerOpen ? "rotate(180deg)" : "rotate(0deg)",
                                  }}
                                />
                              </Box>
                            )}

                            {/* Edit / Done pill */}
                            <Box
                              onClick={() =>
                                isEditing
                                  ? saveEditQuestion(index, answerKey)
                                  : startEditQuestion(q, answerKey)
                              }
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                cursor: "pointer",
                                userSelect: "none",
                                fontWeight: 600,
                                fontSize: "13px",
                                px: 1.5,
                                py: 0.75,
                                borderRadius: "8px",
                                color: isEditing ? "#2e7d32" : "#1c97e3",
                                border: isEditing ? "1px solid #a5d6a7" : "1px solid #b6dcf7",
                                backgroundColor: isEditing ? "#f1f8f2" : "#f3f9ff",
                                transition: "background-color 0.2s ease",
                                "&:hover": { backgroundColor: isEditing ? "#e3f2e5" : "#e7f3fd" },
                              }}
                            >
                              {isEditing ? (
                                <CheckIcon sx={{ fontSize: "18px" }} />
                              ) : (
                                <EditOutlinedIcon sx={{ fontSize: "18px" }} />
                              )}
                              {isEditing ? "Done" : "Edit"}
                            </Box>
                          </Box>

                          {/* Editable answer (edit mode) */}
                          {isEditing && (
                            <Box
                              sx={{
                                mt: 2,
                                p: 2.5,
                                borderRadius: "12px",
                                backgroundColor: "#e8f5e9",
                                border: "1px solid #a5d6a7",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "18px" }} />
                                <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#2e7d32", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                  Answer
                                </Typography>
                              </Box>
                              <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                value={q.answer || ""}
                                onChange={(e) => updateQuestionField(index, "answer", e.target.value)}
                                placeholder="Enter the answer (Markdown supported)..."
                                sx={{ ...commonTextFieldStyle, backgroundColor: "#fff", borderRadius: "8px" }}
                              />
                            </Box>
                          )}

                          {/* Answer (read mode) — smoothly expands */}
                          {!isEditing && answerText && (
                            <Collapse in={isAnswerOpen} timeout={300} unmountOnExit>
                              <Box
                                sx={{
                                  mt: 2,
                                  p: 2.5,
                                  borderRadius: "12px",
                                  backgroundColor: "#e8f5e9",
                                  border: "1px solid #a5d6a7",
                                  position: "relative",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "18px" }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#2e7d32", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                      Answer
                                    </Typography>
                                  </Box>
                                  {q.quiz_id && (
                                    <Tooltip
                                      title={
                                        regeneratingAnswers[q.quiz_id]
                                          ? "Regenerating answer…"
                                          : "Regenerate this answer with a custom prompt"
                                      }
                                      arrow
                                      placement="left"
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "primary.main",
                                            color: "white",
                                            fontSize: 13,
                                            fontWeight: 500,
                                          },
                                        },
                                        arrow: {
                                          sx: { color: "primary.main" },
                                        },
                                      }}
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!!regeneratingAnswers[q.quiz_id]}
                                          onClick={() => openRegenDialog(q.quiz_id, index, q.question)}
                                          sx={{
                                            color: "#1c97e3",
                                            backgroundColor: "#fff",
                                            border: "1px solid #b6dcf7",
                                            "&:hover": { backgroundColor: "#e7f3fd" },
                                            "&.Mui-disabled": {
                                              color: "#1c97e3",
                                              backgroundColor: "#fff",
                                              opacity: 0.7,
                                            },
                                          }}
                                        >
                                          {regeneratingAnswers[q.quiz_id] ? (
                                            <CircularProgress size={16} sx={{ color: "#1c97e3" }} />
                                          ) : (
                                            <AutorenewIcon sx={{ fontSize: "18px" }} />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                                <MarkdownAnswer text={answerText} />
                              </Box>
                            </Collapse>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                );
              })}

              {/* Per-card streaming loaders / errors — shown for sections not yet completed */}
              {streamCards
                .filter((c) => c.status !== "done")
                .map((c) => (
                  <Box
                    key={c.key}
                    sx={{
                      mb: 3,
                      p: { xs: 2.5, md: 3 },
                      borderRadius: "16px",
                      border: `1px solid ${c.status === "error" ? "#ffcdd2" : "#eef2f7"}`,
                      backgroundColor: c.status === "error" ? "#fff8f8" : "#fbfdff",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        label={c.subcategory || "Subtopic"}
                        size="small"
                        sx={{ fontSize: "11px", fontWeight: "bold", backgroundColor: "#e4eefb", color: "#1c97e3", borderRadius: "12px" }}
                      />
                      {c.status === "pending" || c.status === "streaming" ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: "#1c97e3" }} />
                          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1c97e3" }}>
                            {c.status === "streaming" && c.received
                              ? c.total_batches
                                ? `Generating… ${c.received} question${c.received === 1 ? "" : "s"} (batch ${c.batches_done}/${c.total_batches})`
                                : `Generating… ${c.received} question${c.received === 1 ? "" : "s"}`
                              : "Generating…"}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#d32f2f" }}>Failed</Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={isStreaming}
                            onClick={() => retryCard(c)}
                            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, py: 0.2 }}
                          >
                            Retry
                          </Button>
                        </Box>
                      )}
                    </Box>
                    {c.status === "pending" || c.status === "streaming" ? (
                      <>
                        <Skeleton variant="text" width="90%" height={26} />
                        <Skeleton variant="text" width="75%" height={26} />
                        <Skeleton variant="rounded" width="100%" height={70} sx={{ mt: 1.5, borderRadius: "10px" }} />
                      </>
                    ) : (
                      <Typography sx={{ fontSize: "13px", color: "#b71c1c" }}>
                        {c.message || "Generation failed for this section."}
                        {c.retry_after ? ` (suggested retry in ${c.retry_after}s)` : ""}
                      </Typography>
                    )}
                  </Box>
                ))}

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2, borderTop: "1px solid #E0E5F2", pt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isStreaming}
                  onClick={() => {
                    setGeneratedQuestions([]);
                    setSessionKeys([]);
                    setQuestionFeedback({});
                    setStreamCards([]);
                    setGenerationComplete(false);
                    streamQuestionsRef.current = [];
                    streamKeysRef.current = [];
                    localStorage.removeItem("generatedQuestions");
                    localStorage.removeItem("sessionKeys");
                    localStorage.removeItem("questionFeedback");
                  }}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
                >
                  Cancel Questions
                </Button>
                <Button
                  onClick={handleVerify}
                  variant="contained"
                  color="primary"
                  disabled={!generationComplete || generatedQuestions.length === 0}
                  sx={{ textTransform: "none", borderRadius: "12px", px: 4, fontWeight: 600 }}
                >
                  Submit Questions
                </Button>
              </Box>
            </Box>
          )}
        </Box>



        <Dialog
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              p: 1,
            },
          }}
        >
          {/* Title */}
          <DialogTitle sx={{ fontWeight: 700, fontSize: "20px" }}>
            Give Feedback
          </DialogTitle>

          <DialogContent>
            {/* Question Box */}
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "10px",
                p: 2,
                mb: 3,
                fontSize: "14px",
                color: "#555",
              }}
            >
              {currentQuestion?.question}
            </Box>

            {/* Select Issue */}
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Select an issue
            </Typography>

            {/* Chips instead of Radio */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {issues.map((item) => (
                <Chip
                  key={item.value}
                  icon={item.icon}
                  label={item.label}
                  clickable
                  onClick={() => setSelectedIssue(item.value)}
                  sx={{
                    borderRadius: "20px",
                    px: 1,
                    fontWeight: 500,
                    backgroundColor:
                      selectedIssue === item.value ? "#1976d2" : "#f1f1f1",
                    color: selectedIssue === item.value ? "#fff" : "#333",
                    "&:hover": {
                      backgroundColor:
                        selectedIssue === item.value ? "#1565c0" : "#e0e0e0",
                    },
                  }}
                />
              ))}
            </Box>

            {/* Description - only show when Other is selected */}
            {selectedIssue === "Other" && (
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe more about the issue..."
                value={feedbackDescription}
                onChange={(e) => setFeedbackDescription(e.target.value)}
                sx={{
                  mt: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#f9fafb",
                  },
                }}
              />
            )}
          </DialogContent>



          {/* Actions */}
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setFeedbackOpen(false);
                setSelectedIssue("");
                setFeedbackDescription("");
                setCurrentQuestion(null);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#555",
              }}
            >
              CANCEL
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={!selectedIssue || (selectedIssue === "Other" && !feedbackDescription.trim())}
              onClick={async () => {
                setLoading(true);
                try {
                  // Call RefineQuestionBank API
                  const payload = {
                    quiz_id: currentQuestion?.quiz_id,
                    session_key: sessionKeys[0],
                    feedback: feedbackDescription || selectedIssue
                  };

                  console.log("Refining questions with payload:", payload);

                  const res = await api.post("RefineQuestionBank", payload);

                  console.log("Refine API Response:", res);

                  if (res?.data?.status === "success") {
                    // Store feedback for the question using quiz_id
                    const updatedFeedback = {
                      ...questionFeedback,
                      [currentQuestion?.quiz_id]: {
                        issue: selectedIssue,
                        description: feedbackDescription,
                        timestamp: new Date().toISOString()
                      }
                    };
                    setQuestionFeedback(updatedFeedback);
                    localStorage.setItem("questionFeedback", JSON.stringify(updatedFeedback));

                    showSnackbar("success", "Feedback submitted successfully!");
                    setFeedbackOpen(false);
                    setSelectedIssue("");
                    setFeedbackDescription("");
                    setCurrentQuestion(null);
                  } else {
                    console.log("Unexpected refine response:", res?.data);
                    showSnackbar("error", res?.data?.message || "Failed to submit feedback");
                  }
                } catch (error) {
                  console.log("Refine error details:", error);
                  console.log("Refine error response:", error?.response);
                  console.log("Refine error message:", error?.message);
                  showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while refining questions");
                } finally {
                  setLoading(false);
                }
              }}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                px: 3,
                fontWeight: 600,
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Regenerate Answer Prompt Dialog ──
            Opened from the per-question regenerate IconButton. The shared
            dialog owns its preset/custom state and hands back a resolved
            prompt string (or null) on confirm. */}
        <RegenerateAnswerDialog
          open={regenDialog.open}
          onClose={closeRegenDialog}
          loading={!!regeneratingAnswers[regenDialog.quizId]}
          targetSummary={regenDialog.question || null}
          onConfirm={(prompt) => {
            const { quizId, index } = regenDialog;
            closeRegenDialog();
            regenerateAnswer(quizId, index, prompt);
          }}
        />
      </Box>
    </Box>
  );
};

export default AddCategory;



